import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalAction, useUser } from '@gadgetinc/react';
import { api } from '../api';
import googleIcon from '../assets/google.svg';

export default function () {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [institution, setInstitution] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [mode, setMode] = useState('entry'); // 'entry' | 'signup' | 'signin' | 'pending' | 'denied' | 'signup-success'
  const [error, setError] = useState('');
  const [navError, setNavError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fetching, setFetching] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [passwordSetupEmailSent, setPasswordSetupEmailSent] = useState(false);
  const [passwordSetupError, setPasswordSetupError] = useState('');
  const [redirectTo, setRedirectTo] = useState('/');
  const navigate = useNavigate();
  const user = useUser(api);

  const [{ data: passwordSetupData, fetching: sendingPasswordSetup, error: passwordSetupActionError }, sendPasswordSetup] = useGlobalAction(api.sendPasswordSetupForUser);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const normalizeRedirectPath = (value) => {
    if (!value || typeof value !== 'string') return '/';
    if (!value.startsWith('/')) return '/';
    if (value.startsWith('//')) return '/';
    if (value.startsWith('/auth/')) return '/';
    if (
      value === '/sign-in' ||
      value === '/sign-out' ||
      value === '/forgot-password' ||
      value === '/reset-password' ||
      value === '/password-setup' ||
      value === '/setup-password'
    ) return '/';
    return value;
  };
  const buildGoogleAuthUrl = () => {
    const params = new URLSearchParams();
    const redirectTarget = normalizeRedirectPath(redirectTo);
    if (redirectTarget && redirectTarget !== '/') {
      params.set('redirectTo', redirectTarget);
    }
    if (email && email.trim()) {
      params.set('email', email.trim().toLowerCase());
    }
    const query = params.toString();
    return query ? `/auth/google/start?${query}` : '/auth/google/start';
  };

  // Check for invite URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitationToken = urlParams.get('invitationToken');
    const setupToken = urlParams.get('token');
    const loginEmail = urlParams.get('email');
    const inviteEmail = urlParams.get('email');
    const inviteFirstName = urlParams.get('firstName');
    const inviteLastName = urlParams.get('lastName');
    const inviteInstitution = urlParams.get('institution');
    const inviteAccessCode = urlParams.get('accessCode');
    const redirectTarget = normalizeRedirectPath(urlParams.get('redirectTo'));
    setRedirectTo(redirectTarget);

    // Safety redirect: if tokens land on sign-in, route to the correct setup page.
    if (invitationToken) {
      window.location.replace(`/setup-invitation?token=${encodeURIComponent(invitationToken)}`);
      return;
    }
    if (setupToken) {
      navigate(`/password-setup?token=${encodeURIComponent(setupToken)}`, { replace: true });
      return;
    }

    if (loginEmail) {
      setEmail(loginEmail.toLowerCase());
      setMode('signin');
      return;
    }

    // If we have invite parameters, pre-fill the form and go directly to signup
    if (inviteEmail && inviteFirstName && inviteLastName && inviteInstitution) {
      console.log('Invite parameters detected, pre-filling signup form');
      setEmail(inviteEmail);
      setFirstName(inviteFirstName);
      setLastName(inviteLastName);
      setInstitution(inviteInstitution);
      if (inviteAccessCode) {
        setAccessCode(inviteAccessCode);
      }
      setMode('signup');
      
      // Clean up the URL by removing the parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTarget = normalizeRedirectPath(urlParams.get('redirectTo'));
    navigate(redirectTarget || '/', { replace: true });
  }, [navigate, user]);

  // Auto-focus password field when signin mode appears
  useEffect(() => {
    if (mode === 'signin') {
      const passwordField = document.getElementById('password');
      if (passwordField) {
        passwordField.focus();
      }
    }
  }, [mode]);

  const handleContinue = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setFetching(true);
    try {
      const result = await api.checkEmailExists({ email });
      console.log('checkEmail result:', result);
      
      // Add proper null/undefined checks
      if (result && typeof result === 'object') {
        if (result.exists === true) {
          console.log('Email exists, approval status:', result.approvalStatus);
          // Check approval status
          if (result.approvalStatus) {
            switch (result.approvalStatus) {
              case 'approved':
                if (
                  result.hasPassword === false ||
                  result.emailVerified === false ||
                  result.passwordSetupPending === true
                ) {
                  console.log('Approved user needs password setup, showing setup flow');
                  setMode('signin');
                  setNeedsPasswordSetup(true);
                  setPasswordSetupEmailSent(false);
                  setPasswordSetupError('');
                  break;
                }
                console.log('User approved, switching to signin mode');
                setMode('signin');
                setNeedsPasswordSetup(false);
                break;
              case 'pending':
                console.log('User pending approval, switching to pending mode');
                setMode('pending');
                setNeedsPasswordSetup(false);
                break;
              case 'denied':
                console.log('User denied, switching to denied mode');
                setMode('denied');
                setNeedsPasswordSetup(false);
                break;
              default:
                console.log('Unknown approval status, defaulting to signin mode');
                setMode('signin');
                setNeedsPasswordSetup(false);
            }
          } else {
            console.log('No approval status, switching to signin mode');
            setMode('signin');
            setNeedsPasswordSetup(false);
          }
        } else {
          console.log('Email does not exist, switching to signup mode');
          setMode('signup');
          setNeedsPasswordSetup(false);
        }
      } else {
        console.error('Invalid checkEmail result:', result);
        setError('Unable to verify email. Please try again.');
      }
    } catch (err) {
      console.error('checkEmail error:', err);
      setError('Unable to verify email. Please try again.');
    } finally {
      setFetching(false);
    }
  };

  const handleSignup = async (e) => {
    console.log('=== STARTING SIGNUP PROCESS ===');
    console.log('Event received:', e?.type);
    
    e.preventDefault();
    setError('');
    setNavError('');
    
    console.log('handleSignup initiated - Current state:', {
      mode,
      email,
      firstName: firstName?.length ? `${firstName.length} chars` : 'empty',
      lastName: lastName?.length ? `${lastName.length} chars` : 'empty',
      institution: institution?.length ? `${institution.length} chars` : 'empty',
      accessCode: accessCode?.length ? `${accessCode.length} chars` : 'empty',
      fetching,
      signingUp,
      signingIn
    });

    // Safety check - this function should only run for new users
    if (mode !== 'signup') {
      console.error('VALIDATION FAILED: handleSignup called with wrong mode:', mode);
      setError('Something went wrong. Please try again.');
      return;
    }
    console.log('✓ Mode validation passed');

    // Email validation
    if (!email || !email.trim()) {
      console.error('VALIDATION FAILED: Email is missing');
      setError('Email is required');
      return;
    }
    console.log('✓ Email validation passed:', email);

    // Validate required fields with detailed logging
    if (!firstName || !firstName.trim()) {
      console.error('VALIDATION FAILED: First name is missing or empty');
      setError('Please enter your first name');
      return;
    }
    console.log('✓ First name validation passed:', firstName.trim());

    if (!lastName || !lastName.trim()) {
      console.error('VALIDATION FAILED: Last name is missing or empty');
      setError('Please enter your last name');
      return;
    }
    console.log('✓ Last name validation passed:', lastName.trim());

    if (!institution || !institution.trim()) {
      console.error('VALIDATION FAILED: Institution is missing or empty');
      setError('Please enter your institution');
      return;
    }
    console.log('✓ Institution validation passed:', institution.trim());

    console.log('=== ALL VALIDATIONS PASSED - PROCEEDING WITH API CALL ===');

    // Set loading state
    console.log('Setting signingUp state to true');
    setSigningUp(true);

    try {
      const signupData = {
        user: {
          email: email.trim(), 
          firstName: firstName.trim(), 
          lastName: lastName.trim(), 
          institution: institution.trim()
        },
        accessCode: accessCode && accessCode.trim() ? accessCode.trim() : undefined
      };

      console.log('Prepared signup data:', {
        user: {
          email: signupData.user.email,
          firstName: signupData.user.firstName,
          lastName: signupData.user.lastName,
          institution: signupData.user.institution
        },
        accessCode: signupData.accessCode ? 'PROVIDED' : 'NOT PROVIDED'
      });

      console.log('Making API call to api.user.signUp...');
      const result = await api.user.signUp(signupData);
      
      console.log('=== API CALL SUCCESSFUL ===');
      console.log('Signup result received:', {
        success: result?.success,
        hasPasswordSetupToken: !!result?.passwordSetupToken,
        skipEmail: result?.skipEmail,
        resultKeys: result ? Object.keys(result) : 'no result'
      });
      
      // Check if we should redirect to password setup
      if (result && result.passwordSetupToken && result.skipEmail) {
        console.log('Password setup token received, preparing redirect to password setup');
        const redirectUrl = `/password-setup?token=${result.passwordSetupToken}`;
        console.log('Redirect URL:', redirectUrl);
        navigate(redirectUrl);
        return;
      }
      
      // Show success message based on whether access code was provided
      const hasAccessCode = accessCode && accessCode.trim().length > 0;
      console.log('Access code status:', hasAccessCode ? 'PROVIDED' : 'NOT PROVIDED');
      
      const successMsg = hasAccessCode 
        ? 'Registration successful! Check your email to set your password and complete your account setup.'
        : 'Registration submitted! Your request has been submitted for admin approval. You\'ll receive an email notification once approved.';
      
      console.log('Success message prepared:', successMsg);
      setSuccessMessage(successMsg);
      
      // Reset form fields
      console.log('Resetting form fields...');
      setFirstName('');
      setLastName('');
      setInstitution('');
      setAccessCode('');
      setError('');
      setNavError('');
      
      // Switch to success mode
      console.log('Switching to signup-success mode');
      setMode('signup-success');
      
      console.log('=== SIGNUP PROCESS COMPLETED SUCCESSFULLY ===');
      
    } catch (err) {
      console.error('=== SIGNUP ERROR CAUGHT ===');
      console.error('Error object:', err);
      console.error('Error message:', err?.message);
      console.error('Error stack:', err?.stack);
      console.error('Error type:', typeof err);
      
      let errorMessage = 'Failed to create account. Please try again.';
      
      // Handle different types of errors with detailed logging
      if (err && err.message) {
        console.log('Processing error message:', err.message);
        
        if (err.message.includes('unique constraint')) {
          console.log('Detected unique constraint violation');
          errorMessage = 'This email is already registered. Please sign in instead.';
          console.log('Switching to signin mode due to unique constraint');
          setMode('signin');
        } else if (err.message.includes('Invalid access code')) {
          console.log('Detected invalid access code error');
          errorMessage = 'Invalid access code. Please check the code and try again, or leave it blank to request admin approval.';
        } else if (err.message.includes('validation')) {
          console.log('Detected validation error');
          errorMessage = `Validation error: ${err.message}`;
        } else if (err.message.includes('network')) {
          console.log('Detected network error');
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else {
          console.log('Generic error, using original message');
          errorMessage = `Error: ${err.message}`;
        }
      } else {
        console.error('Error object has no message property');
      }
      
      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
      
    } finally {
      console.log('Setting signingUp state to false');
      setSigningUp(false);
      console.log('=== SIGNUP PROCESS ENDED ===');
    }
  };

  const handleSignin = async (e) => {
    e.preventDefault();
    setError('');
    setNavError('');
    setNeedsPasswordSetup(false);
    setPasswordSetupEmailSent(false);
    setPasswordSetupError('');

    console.debug('handleSignin start, email:', email);

    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setSigningIn(true);
    setShowLoadingOverlay(true); // Show overlay immediately
    
    try {
      console.debug('Attempting signin for email:', email);
      await api.user.signIn({ email, password });
      const redirectTarget = normalizeRedirectPath(redirectTo);
      console.debug('Signin successful, redirecting to', redirectTarget);
      
      // Use window.location for full page reload to ensure session is properly loaded
      // The overlay will stay visible until the new page loads
      setTimeout(() => {
        window.location.href = redirectTarget;
      }, 300);
      
    } catch (err) {
      console.error('Signin error:', err);
      let errorMessage = 'Invalid credentials. Please try again.';
      
      // Check if the error indicates no password is set
      const errorMsg = err && err.message ? err.message.toLowerCase() : '';
      const isNoPasswordError = 
        errorMsg.includes('password not set') ||
        errorMsg.includes('no password') ||
        errorMsg.includes('password setup required') ||
        errorMsg.includes('must set password') ||
        errorMsg.includes('password is required');
      
      if (isNoPasswordError) {
        console.log('Detected no password set error, showing password setup flow');
        setNeedsPasswordSetup(true);
        setPasswordSetupEmailSent(false);
        setPasswordSetupError('');
        setShowLoadingOverlay(false);
        setSigningIn(false);
        return;
      }
      
      // Handle specific error messages
      if (err && err.message) {
        if (err.message.includes('Invalid email or password')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (err.message.includes('GGT_INVALID_EMAIL_PASSWORD')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setShowLoadingOverlay(false);
      setSigningIn(false);
    } finally {
      // Only reset if we're still on this page (didn't redirect)
      // The setTimeout in the success path will handle the redirect
    }
  };

  const handleSendPasswordSetup = async () => {
    setPasswordSetupError('');
    
    try {
      console.log('Sending password setup email for:', email);
      await sendPasswordSetup({ email });
      
      console.log('Password setup email sent successfully');
      setPasswordSetupEmailSent(true);
      setPasswordSetupError('');
    } catch (err) {
      console.error('Password setup error:', err);
      
      let errorMessage = 'Failed to send password setup email. Please try again.';
      
      if (err && err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setPasswordSetupError(errorMessage);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);

    // Validate email
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    if (!emailRegex.test(resetEmail)) {
      setResetError('Please enter a valid email address');
      return;
    }

    setFetching(true);
    try {
      console.log('Sending password reset email to:', resetEmail);
      await api.user.sendResetPassword({ email: resetEmail });
      
      console.log('Password reset email sent successfully');
      setResetSuccess(true);
      setResetError('');
      
      // Auto-close modal and reset after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(false);
        setResetEmail('');
        setMode('entry');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      if (err && err.message) {
        if (err.message.includes('not found') || err.message.includes('does not exist')) {
          errorMessage = 'No account found with this email address.';
        } else if (err.message.includes('not approved') || err.message.includes('pending')) {
          errorMessage = 'Your account is pending approval. Please wait for admin approval before resetting your password.';
        } else if (err.message.includes('denied')) {
          errorMessage = 'Your account access has been denied. Please contact support.';
        } else {
          errorMessage = `Error: ${err.message}`;
        }
      }
      
      setResetError(errorMessage);
    } finally {
      setFetching(false);
    }
  };

  const handleBackToEntry = () => {
    setMode('entry');
    setPassword('');
    setFirstName('');
    setLastName('');
    setInstitution('');
    setAccessCode('');
    setError('');
    setNavError('');
    setSuccessMessage('');
    setNeedsPasswordSetup(false);
    setPasswordSetupEmailSent(false);
    setPasswordSetupError('');
  };

  const handleModeSwitch = (newMode) => {
    console.log('Mode switch requested from', mode, 'to', newMode);
    setMode(newMode);
    setPassword(''); // Clear password when switching modes
    setFirstName('');
    setLastName('');
    setInstitution('');
    setAccessCode('');
    setError('');
    setNavError('');
    setSuccessMessage('');
    setNeedsPasswordSetup(false);
    setPasswordSetupEmailSent(false);
    setPasswordSetupError('');
  };

  const isAutoApprovalDomain = email.endsWith('@southwest.tn.edu');

  return (
    <div className="auth-page">
      <div className="auth-card">

        <div className="auth-header">
          <h1>
            Elevation EMS AI
          </h1>
          <p className="subtitle">
            AI-Powered Tools for EMS Education
          </p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {navError && (
          <div className="auth-warning">
            {navError}
          </div>
        )}

        {mode === 'entry' && (
          <div className="auth-mode-container">
            <div className="google-oauth-section">
              <button
                type="button"
                className="button button-outline google-oauth-button"
                onClick={() => window.location.href = buildGoogleAuthUrl()}
                disabled={fetching || signingUp || signingIn}
              >
                <img src={googleIcon} alt="Google logo" className="google-icon" />
                Continue with Google
              </button>

              <div className="form-divider with-text">
                <span>or</span>
              </div>
            </div>

            <form onSubmit={handleContinue} className="auth-form">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="username email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  placeholder="Enter your email address"
                  disabled={fetching}
                />
              </div>

              <div className="auth-button-container">
                <button
                  type="submit"
                  disabled={fetching || signingUp || signingIn}
                  className="button button-primary"
                >
                  {(fetching || signingUp || signingIn) ? 'Please wait...' : 'Continue'}
                </button>
              </div>
            </form>
          </div>
        )}

        {mode === 'signup' && (
          <div className="auth-mode-container">
            <div className="auth-header">
              <h2>
                Create Account
              </h2>
              <p className="subtitle">
                Register for access to Elevation EMS
              </p>
              {isAutoApprovalDomain && (
                <div className="auth-success">
                  ✓ Your email domain is pre-approved
                </div>
              )}
            </div>

            <div className="auth-email-display">
              <strong>Email:</strong> {email}
            </div>

            <form onSubmit={handleSignup} className="auth-form">
              <div className="auth-field-group">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    disabled={fetching || signingUp || signingIn}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    disabled={fetching || signingUp || signingIn}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="institution" className="form-label">
                    Institution
                  </label>
                  <input
                    type="text"
                    id="institution"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    placeholder="Enter your school or organization"
                    disabled={fetching || signingUp || signingIn}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="accessCode" className="form-label">
                    Access Code (Optional)
                  </label>
                  <input
                    type="text"
                    id="accessCode"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter access code if you have one"
                    disabled={fetching || signingUp || signingIn}
                  />
                  <p className="auth-field-help">
                    If you have an access code, enter it for immediate approval. Otherwise, your request will be submitted for admin review.
                  </p>
                </div>
              </div>

              <div className="auth-button-container">
                <button
                  type="submit"
                  disabled={fetching || signingUp || signingIn}
                  className="button button-primary"
                >
                  {(fetching || signingUp || signingIn) ? 'Registering...' : 'Register'}
                </button>
              </div>

              <div className="auth-button-container">
                <button
                  type="button"
                  onClick={handleBackToEntry}
                  className="button button-secondary"
                  disabled={fetching || signingUp || signingIn}
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        )}

        {mode === 'signin' && (
          <div className="auth-mode-container">
            <div className="auth-header">
              <h2>
                Sign In
              </h2>
              <p className="subtitle">
                Welcome back! Please sign in to your account
              </p>
            </div>

            <div className="auth-email-display">
              <strong>Email:</strong> {email}
            </div>

            {needsPasswordSetup ? (
              <div className="auth-mode-container" style={{ marginTop: '1rem' }}>
                <div className="auth-info-box">
                  <div className="auth-info-icon">✉️</div>
                  <div className="auth-info-content">
                    <h3 className="auth-info-title">Password Setup Required</h3>
                    <p className="auth-info-message">
                      Your account doesn't have a password set yet. This is normal for newly invited users. 
                      Click the button below to receive password setup instructions via email.
                    </p>
                  </div>
                </div>

                {passwordSetupError && (
                  <div className="auth-error" style={{ marginTop: '1rem' }}>
                    {passwordSetupError}
                  </div>
                )}

                {passwordSetupEmailSent ? (
                  <div className="auth-success" style={{ marginTop: '1rem', padding: '1rem' }}>
                    ✅ Password setup instructions have been sent to <strong>{email}</strong>. Please check your inbox and follow the link to set your password.
                  </div>
                ) : null}

                <div className="auth-button-container" style={{ marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    onClick={handleSendPasswordSetup}
                    disabled={sendingPasswordSetup || passwordSetupEmailSent}
                    className="button button-primary"
                  >
                    {sendingPasswordSetup ? 'Sending...' : passwordSetupEmailSent ? 'Email Sent' : 'Send Password Setup Email'}
                  </button>
                </div>

                {passwordSetupEmailSent && (
                  <div className="auth-link-section" style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={handleSendPasswordSetup}
                      className="auth-link"
                      disabled={sendingPasswordSetup}
                      style={{ fontSize: '0.9rem' }}
                    >
                      Didn't receive it? Send again
                    </button>
                  </div>
                )}

                <div className="auth-button-container" style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setNeedsPasswordSetup(false);
                      setPasswordSetupEmailSent(false);
                      setPasswordSetupError('');
                      setPassword('');
                    }}
                    className="button button-secondary"
                    disabled={sendingPasswordSetup}
                  >
                    Back to Sign In
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSignin} className="auth-form">
                <div className="auth-field-group">
                  <div className="form-group">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={fetching || signingUp || signingIn}
                    />
                  </div>
                </div>

                <div className="auth-link-section" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setResetEmail(email);
                      setResetError('');
                      setResetSuccess(false);
                    }}
                    className="auth-link"
                    disabled={fetching || signingUp || signingIn}
                    style={{ fontSize: '0.9rem' }}
                  >
                    Forgot your password?
                  </button>
                </div>

                <div className="auth-button-container">
                  <button
                    type="submit"
                    disabled={fetching || signingUp || signingIn}
                    className="button button-primary"
                  >
                    {(fetching || signingUp || signingIn) ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>

                <div className="auth-button-container">
                  <button
                    type="button"
                    onClick={handleBackToEntry}
                    className="button button-secondary"
                    disabled={fetching || signingUp || signingIn}
                  >
                    Back
                  </button>
                </div>
              </form>
            )}

            <div className="auth-link-section">
              <p className="auth-link-text">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => handleModeSwitch('signup')}
                  className="auth-link"
                  disabled={fetching || signingUp || signingIn}
                >
                  Create one here
                </button>
              </p>
            </div>
          </div>
        )}

        {mode === 'pending' && (
          <div className="auth-mode-container">
            <div className="auth-header">
              <h2>
                Approval Pending
              </h2>
              <p className="subtitle">
                Your account is awaiting approval from an administrator
              </p>
            </div>

            <div className="auth-email-display">
              <strong>Email:</strong> {email}
            </div>

            <div className="auth-warning">
              ⏳ Your account is pending approval. You'll receive an email notification once approved.
            </div>

            <div className="auth-button-container">
              <button
                type="button"
                onClick={handleBackToEntry}
                className="button button-secondary full-width"
                disabled={fetching || signingUp || signingIn}
              >
                Try Different Email
              </button>
            </div>
          </div>
        )}

        {mode === 'denied' && (
          <div className="auth-mode-container">
            <div className="auth-header">
              <h2>
                Access Denied
              </h2>
              <p className="subtitle">
                Your account application has been declined
              </p>
            </div>

            <div className="auth-email-display">
              <strong>Email:</strong> {email}
            </div>

            <div className="auth-error">
              ❌ Your access request has been denied. Please contact the administrator for more information.
            </div>

            <div className="auth-button-container">
              <button
                type="button"
                onClick={handleBackToEntry}
                className="button button-secondary full-width"
                disabled={fetching || signingUp || signingIn}
              >
                Try Different Email
              </button>
            </div>
          </div>
        )}

        {mode === 'signup-success' && (
          <div className="auth-mode-container">
            <div className="auth-header">
              <h2>
                ✅ Registration Successful!
              </h2>
              <p className="subtitle">
                Your account has been created
              </p>
            </div>

            <div className="auth-success" style={{ marginBottom: '2rem', padding: '1.5rem', textAlign: 'center' }}>
              {successMessage}
            </div>

            <div className="auth-button-container">
              <button
                type="button"
                onClick={handleBackToEntry}
                className="button button-primary full-width"
                disabled={fetching || signingUp || signingIn}
              >
                Continue to Sign In
              </button>
            </div>
          </div>
        )}

        {showForgotPassword && (
          <div className="auth-modal-overlay" onClick={() => {
            setShowForgotPassword(false);
            setResetError('');
            setResetSuccess(false);
          }}>
            <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="auth-header">
                <h2>Reset Password</h2>
                <p className="subtitle">
                  Enter your email address and we'll send you a password reset link
                </p>
              </div>

              {resetSuccess ? (
                <div className="auth-success" style={{ marginBottom: '1rem', padding: '1rem' }}>
                  ✅ Password reset email sent! Please check your inbox.
                </div>
              ) : (
                <>
                  {resetError && (
                    <div className="auth-error" style={{ marginBottom: '1rem' }}>
                      {resetError}
                    </div>
                  )}

                  <form onSubmit={handleForgotPassword} className="auth-form">
                    <div className="form-group">
                      <label htmlFor="reset-email" className="form-label">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="reset-email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value.toLowerCase())}
                        placeholder="Enter your email address"
                        disabled={fetching}
                        autoFocus
                      />
                    </div>

                    <div className="auth-button-container">
                      <button
                        type="submit"
                        disabled={fetching}
                        className="button button-primary"
                      >
                        {fetching ? 'Sending...' : 'Send Reset Link'}
                      </button>
                    </div>

                    <div className="auth-button-container">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setResetError('');
                          setResetSuccess(false);
                        }}
                        className="button button-secondary"
                        disabled={fetching}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {showLoadingOverlay && (
          <div className="loading-overlay">
            <div className="loading-card">
              <div className="loading-spinner-large"></div>
              <h2 className="loading-title">Signing you in...</h2>
              <p className="loading-subtitle">Please wait while we establish your session</p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.2s ease-in-out;
        }

        .loading-card {
          background: white;
          padding: 3rem 4rem;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          text-align: center;
          max-width: 400px;
          animation: slideUp 0.3s ease-out;
        }

        .loading-spinner-large {
          width: 60px;
          height: 60px;
          border: 4px solid #e1e8ed;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          margin: 0 auto 1.5rem;
          animation: spin 1s linear infinite;
        }

        .loading-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #2c3e50;
        }

        .loading-subtitle {
          margin: 0;
          font-size: 1rem;
          color: #7f8c8d;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .auth-info-box {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          border: 2px solid #2196f3;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1rem;
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }

        .auth-info-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .auth-info-content {
          flex: 1;
        }

        .auth-info-title {
          margin: 0 0 0.5rem 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1976d2;
        }

        .auth-info-message {
          margin: 0;
          font-size: 0.95rem;
          color: #1565c0;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}
